import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {backgroundColorValue, foregroundColorValue, desiredTextColor} from '../../javascripts/modules/colors.js';

class KeliBrowseRow extends PolymerElement {
  static get template() {
    return html`
<style>
  :host {
    display: table-row;
    position: var(--keli-browse-row-position, absolute);
    left: 0;
    right: 0;
    white-space: var(--keli-browse-row-white-space, nowrap);
    cursor: pointer;
    transform-style: preserve-3d;
    transform-origin: center;
    box-sizing: border-box;
    min-height: 24px;
    height: var(--keli-browse-row-height, 24px);
    color: var(--keli-text);
  }

  :host(.deleted) {
    display: none;
  }

  ::slotted(.cell) {
    height: 24px; /* For table cells, height is basically min-height */
    min-width: 150px;
    width: 150px;
    max-width: 150px;
    display: table-cell;
    white-space: var(--keli-browse-row-white-space, nowrap);
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    box-sizing: border-box;
    padding: 2px 6px 0 6px;
    border-left: 1px solid var(--keli-browse-cell-border-left);
    border-right: 1px solid var(--keli-browse-cell-border-right);
  }

  ::slotted(.cell:first-of-type) {
    border-left: none !important;
  }

  ::slotted(.cell.right) {
    text-align: right;
  }

  ::slotted(.cell.left) {
    text-align: left;
  }

  ::slotted(.cell.center) {
    text-align: center;
  }

  /* Apply color styles in the element because rows are attached a lot */
  :host(.selected) {
    box-shadow: inset 0 0 0 1px var(--keli-browse-row-focused-border);
  }

  :host(.odd) {
    background-color: var(--keli-browse-row-background-odd);
  }
</style>

<slot></slot>`;
  }

  static get is() {
    return 'keli-browse-row';
  }

  static get properties() {
    return {
      _currentOffset: {type: Number},
      browse: Object,
      deleted: Boolean,
      position: {type: Number, reflectToAttribute: true, observer: 'positionChanged'},
      rowHeight: {type: Number, value: 24},
      rowId: {type: String, reflectToAttribute: true},
      selected: {type: Boolean, observer: 'selectedChanged'}
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.handleClick);
    this.addEventListener('dblclick', this.handleDoubleClick);
    this.addEventListener('browseCellAttributes', this.handleBrowseCellAttributes);

    if (this.browse) {
      let cells = this.querySelectorAll('.cell');
      let headerCells = this.browse.$.header.querySelectorAll('keli-browse-header-cell');

      cells.forEach(cell => {
        for (let i = 0; i < headerCells.length; ++i) {
          if (headerCells[i].keliname === cell.dataset.keliname) {
            this.setCellAttributes(cell, {
              width: headerCells[i].width,
              justify: headerCells[i].justify
            });
            break;
          }
        }
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('dblclick', this.handleDoubleClick);
    this.removeEventListener('browseCellAttributes', this.handleBrowseCellAttributes);

    this.browse = null;
  }

  updateRowPositionOffset(newOffset) {
    this._currentOffset = (newOffset !== undefined ? newOffset : this.browse?.$.controller?.getPositionOffsetForRow(this.position));
    this.style.top = `${this._currentOffset}px`;
  }

  positionChanged(newValue) {
    let isEven = (newValue % 2 === 0);
    this.classList.toggle('odd', !isEven);
  }

  selectedChanged(newValue) {
    this.classList.toggle('selected', newValue);
  }

  color(background, color) {
    const newColor = color || '';
    const newBackground = background || '';

    let changedBackground;
    if (this.dataset.background !== newBackground) {
      this.dataset.background = newBackground;
      changedBackground = backgroundColorValue(newBackground);
      this.style.backgroundColor = changedBackground;
    }

    if (this.dataset.color !== newColor) {
      this.dataset.color = newColor;
      this.style.color = foregroundColorValue(newColor, null, true, this.style.backgroundColor) || desiredTextColor(this.dataset.background);
    }

    if (changedBackground !== undefined) {
      afterNextRender(this, () => {
        const cells = this.querySelectorAll('.cell');
        for (const cell of cells) {
          this.cellColor(cell, cell.dataset.background, cell.dataset.color, true);
        }
      });
    }
  }

  cellColor(cell, background, color, force = false) {
    const newColor = color || '';
    const newBackground = background || '';

    if (force || cell.dataset.background !== newBackground) {
      cell.dataset.background = newBackground;
      cell.style.backgroundColor = backgroundColorValue(cell.dataset.background);
    }
    if (force || cell.dataset.color !== newColor) {
      const cellOrRowBackground = cell.style.backgroundColor || this.style.backgroundColor;
      cell.dataset.color = newColor;
      cell.style.color = (!cellOrRowBackground && !newColor ? '' : foregroundColorValue(cell.dataset.color, null, true, cellOrRowBackground) || desiredTextColor(cellOrRowBackground));
    }
  }

  handleBrowseCellAttributes(event) {
    const cell = this.querySelector(`[data-keliname="${event.detail.keliname}"]`);
    if (cell) {
      this.setCellAttributes(cell, {width: event.detail.width, justify: event.detail.justify});
    }
  }

  setCellAttributes(cell, attributes) {
    if (!cell) {
      return;
    }
    if (attributes.width) {
      cell.style.width = cell.style.maxWidth = cell.style.minWidth = attributes.width + 'px';
    }
    if (attributes.justify) {
      cell.classList.remove('left', 'center', 'right');
      cell.classList.add(attributes.justify);
    }
  }

  handleClick(event) {
    const multiSelectElementName = this.browse?.$.controller?.multiSelectElementName;
    const eventTarget = getEventTarget(event);
    if (multiSelectElementName && eventTarget?.dataset?.keliname === multiSelectElementName) {
      eventTarget?.firstElementChild?.shadowRoot?.querySelector('#toggle').click();
      event.preventDefault();
      event.stopPropagation();
      return;
    }


    if (this.selected && KeliInterface.isTouchDevice) {
      event.stopPropagation();
      this.activateRow(event);
    } else {
      this.browse?.dispatchEvent(new CustomEvent('setSelectedRow', {
        detail: {element: this}
      }));
    }
  }

  handleDoubleClick(event) {
    this.activateRow(event);
  }

  activateRow(event) {
    this.browse?.dispatchEvent(new CustomEvent('activateRow', {
      detail: {element: this, event: event, rowid: this.rowId}
    }));
  }

  calculateTextWidth(cell) {
    if (!cell.dataset.calculatedTextWidth) {
      cell.dataset.calculatedTextWidth = measureTextWidth(cell.textContent, cell) + 5;
    }
    return parseInt(cell.dataset.calculatedTextWidth, 10);
  }
}

customElements.define(KeliBrowseRow.is, KeliBrowseRow);
export {KeliBrowseRow};
